import { useEffect, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@quebon/ui/components/button'
import type { ReviewNoteItemView, ReviewNoteView } from '@/api/lcReviewNote'
import { canExcludeReviewNoteItems } from '@/utils/reviewNoteSources'
import { QuestionGradingList } from './QuestionGradingList'
import { ReviewNoteExcludeButton } from './ReviewNoteExcludeButton'
import { parseStoredAnswer } from './component-details/parts/answerArray'

interface Props {
  reviewNote: ReviewNoteView
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  disabled?: boolean
  isExcluding?: boolean
  onExclude: (item: ReviewNoteItemView) => void | Promise<unknown>
}

export function ReviewNoteExclusionList({ reviewNote, expanded: requestedExpanded, onExpandedChange, disabled = false, isExcluding = false, onExclude }: Props) {
  const { t } = useTranslation('coaching')
  const contentId = useId()
  const editorRef = useRef<HTMLElement>(null)
  const expanded = requestedExpanded && canExcludeReviewNoteItems(reviewNote)
  useEffect(() => {
    if (expanded) editorRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
  }, [expanded])
  if (!reviewNote.canEditItems || reviewNote.items.length === 0) return null
  const minimumReached = reviewNote.totalCount <= 1 || reviewNote.items.length <= 1
  return (
    <section ref={editorRef} className="min-w-0 rounded-lg border border-border">
      <div className="flex flex-wrap items-center justify-between gap-2 p-3">
        <p className="text-xs text-muted-foreground">{t(minimumReached ? 'reviewNote.minimumQuestion' : 'reviewNote.exclusionGuide')}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isExcluding || minimumReached}
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => onExpandedChange(!expanded)}
        >
          {t(expanded ? 'reviewNote.closeQuestionEditor' : 'reviewNote.editQuestions')}
          {expanded ? <ChevronUp data-icon="inline-end" /> : <ChevronDown data-icon="inline-end" />}
        </Button>
      </div>
      <div id={contentId} hidden={!expanded}>
        {expanded ? (
          <QuestionGradingList
            key={reviewNote.setId}
            presentation="single"
            canGrade={false}
            isSubmitting={isExcluding}
            resultHideSolveDescription
            items={reviewNote.items.map((item) => ({ ...item, studentAnswer: parseStoredAnswer(item.answer) }))}
            renderItemActions={(question, index) => {
              const item = reviewNote.items.find((candidate) => candidate.quizId === question.quizId)
              return item ? (
                <ReviewNoteExcludeButton
                  questionNumber={index + 1}
                  disabled={disabled}
                  pending={isExcluding}
                  minimumReached={minimumReached}
                  onClick={() => { void onExclude(item) }}
                />
              ) : null
            }}
          />
        ) : null}
      </div>
    </section>
  )
}
